import shortid from 'shortid';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import maxBy from 'lodash/maxBy';
import difference from 'utils/difference';
import { put, fork, take, select, race } from 'redux-saga/effects';
import { takeFetch } from 'redux/helpers';
import * as serviceActions from '../../../../services/actions';
import * as actions from './actions';
import currentBoard from './currentBoard/saga';
import {getTotalChanges} from '../../../../../helpers';
import {projectIsTypeKanban} from '../../selectors';


function* upsertBoard() {
  const { success } = yield takeFetch(serviceActions.api.configuration.project.UPSERT_BOARD);
  if (success) {
    const { currentBoard, boardOptions, systemBoard } = yield select(state => state.configuration.project.editor.board);
    yield put(actions.save.end({ changes: getTotalChanges(boardOptions) - currentBoard.changes, id: success.data.id }));
    if(currentBoard.value.isRemoved) {
      if(boardOptions.length) {
        yield put(actions.currentBoard.init.begin(boardOptions[0].$$localId));
      } else if(systemBoard) {
        yield put(actions.currentBoard.init.begin(systemBoard.$$localId));
      } else {
        yield put(actions.currentBoard.empty());
      }
    }
    ttb.Notifications.ShowSaveSuccessMessage('Project Configuration', 'All the changes have been saved');
  } else {
    ttb.Notifications.ShowErrorMessage('Project Configuration', 'The changes have not been saved');
  }
}

function* getBoards({ selectedIndex }) {
  const { success } = yield takeFetch(serviceActions.api.configuration.project.GET_BOARDS);
  if (success) {
      const receivedBoards = success.data.boards;
      receivedBoards.sort((a, b) => a.order - b.order);

      const boardOptions = receivedBoards.map(value => ({
      value: cloneDeep(value),
      $$localId: shortid.generate(),
      changes: 0,
      timelineType: value.isTTColorsCategoryBased ? 1 : 2,
    }));
    const systemBoard = find(boardOptions, x => x.value.isReserveredBySystem);
    yield put(actions.init.end({
      systemBoard,
      boardOptions,
      boards: receivedBoards,
      groupedOptions: success.data.groupedOptions,
      simpleOptions: success.data.simpleOptions,
      containerOptions: success.data.containerOptions,
      itemOptions: success.data.itemOptions,
      statuses: success.data.statuses,
      items: success.data.items,
    }));
    const isProjectKanban = yield select(projectIsTypeKanban);
    const option = boardOptions[selectedIndex] || isProjectKanban ? systemBoard : boardOptions.find(board => !board.value.isReserveredBySystem);
    if(option) {
      yield put(actions.currentBoard.init.begin(option.$$localId));
      yield put(actions.invalidate.begin());
    } else {
      yield put(actions.currentBoard.empty());
    }
  }
}

function* init() {
  while (true) {
    const { projectId, selectedIndex } = yield take(actions.INIT.BEGIN);
    yield put(serviceActions.api.configuration.project.getBoards.request({
      projectId,
    }));
    yield fork(getBoards, { selectedIndex });
  }
}

function* checkValidation() {
  const { errors } = yield take(actions.currentBoard.CHECK_VALIDATION.END);
  if (!errors) {
    const { projectId, currentBoard: selectedBoard } = yield select(state => state.configuration.project.editor.board);
    const boardValue = cloneDeep(selectedBoard.value);
    yield put(serviceActions.api.configuration.project.upsertBoard.request({
      projectId,
      board: boardValue,
    }));
    yield fork(upsertBoard);
  } else {
    yield put(actions.cancelSave());
  }
}

function* save() {
  while (true) {
    yield take(actions.SAVE.BEGIN);
    yield fork(checkValidation);
    yield put(actions.currentBoard.checkValidation.begin());
  }
}

function* add() {
  while (true) {
    yield take(actions.ADD.BEGIN);
    const { boardOptions: previosBoards } = yield select(state => state.configuration.project.editor.board);
    const maxOrderStatus = maxBy(previosBoards, x => x.value.order);
    const order = maxOrderStatus ? maxOrderStatus.value.order + 1 : 1;
    const localId = shortid.generate();
    const boardOptions = [
      ...previosBoards,
      {
        $$localId: localId,
        added: true,
        value: {
          order,
          isActive: true,
          name: `New Board ${previosBoards.length + 1}`,
          columns: [{
            name: 'New Column 1',
            config: [],
          }],
        },
        timelineType: 1,
      },
    ];
    yield put(actions.add.end({ boardOptions }));
    yield put(actions.currentBoard.init.begin(localId));
  }
}

function* remove() {
  while (true) {
    const { localId } = yield take(actions.REMOVE.BEGIN);
    const { boardOptions: previosBoards, systemBoard } = yield select(state => state.configuration.project.editor.board);
    const boardOptions = previosBoards.filter(x => x.$$localId !== localId);
    yield put(actions.remove.end({ boardOptions }));

    const isProjectKanban = yield select(projectIsTypeKanban);
    const option = boardOptions[boardOptions.length - 1] || isProjectKanban ? systemBoard : null;
    if(option) {
      yield put(actions.currentBoard.init.begin(option.$$localId));
    } else {
      yield put(actions.currentBoard.empty());
    }
  }
}

function* invalidate() {
  while (true) {
    yield race([
      take(actions.INVALIDATE.BEGIN),
      take(actions.ADD.END),
      take(actions.REMOVE.END),
      take(actions.currentBoard.SET_NAME),
      take(actions.currentBoard.SET_IS_ACTIVE),
      take(actions.currentBoard.SET_ORDER),
      take(actions.currentBoard.SET_IS_SPRINT_BOARD),
      take(actions.currentBoard.SET_TIMELINE_TYPE),
      take(actions.currentBoard.ADD_COLUMN.END),
      take(actions.currentBoard.ADD_ROW.END),
      take(actions.currentBoard.REMOVE_COLUMN.END),
      take(actions.currentBoard.REMOVE_ROW.END),
      take(actions.currentBoard.SET_ITEM_TYPE.END),
      take(actions.currentBoard.SET_STATYS.END),
      take(actions.currentBoard.SET_COLUMN_NAME.END),
      take(actions.currentBoard.SET_IS_GROUPED_BOARD.END),
      take(actions.currentBoard.SET_GROUP_ITEM_TYPE.END),
      take(actions.currentBoard.SET_REMOVED),
    ]);
    const {
      boards,
      boardOptions: previosBoards,
      currentBoard: selectedBoard,
       } = yield select(state => state.configuration.project.editor.board);
    const newBoards = previosBoards.map((x) => {
      const res = x.$$localId === selectedBoard.$$localId && !isEqual(x.value, selectedBoard.value)
        ? { ...selectedBoard }
        : x;
      const boardValue = find(boards, y => y.id === res.value.id);
      const changes = boardValue ? difference(res.value, boardValue) : 1;
      return res.changes === changes ? res : {
        ...res,
        changes,
      };
    });
    const boardOptions = isEqual(newBoards, previosBoards)
      ? previosBoards
      : newBoards;

    const changes = getTotalChanges(boardOptions);
    yield put(actions.invalidate.end({ boardOptions, changes }));

    const newSelectedBoard = find(boardOptions, x => x.$$localId === selectedBoard.$$localId);
    if (newSelectedBoard && newSelectedBoard.changes !== selectedBoard.changes) {
      yield put(actions.currentBoard.invalidateChanges({ changes: newSelectedBoard.changes }));
    }
    yield put(actions.currentBoard.checkValidation.begin());
  }
}

function* resetChanges() {
  while (true) {
    yield take(actions.RESET_CHANGES.BEGIN);
    const {
      boards,
      boardOptions,
      currentBoard,
    } = yield select(state => state.configuration.project.editor.board);
    const boardValue = boards.find(board => board.id === currentBoard.value.id);
    const resetBoard = {
      ...currentBoard,
      value: cloneDeep(boardValue),
      changes: 0,
      timelineType: boardValue.isTTColorsCategoryBased ? 1 : 2,
    };
    yield put(actions.resetChanges.end({
      resetBoard,
      changes: getTotalChanges(boardOptions) - currentBoard.changes,
    }));
    yield put(actions.currentBoard.init.begin(resetBoard.$$localId));
  }
}

export default function* board() {
  yield fork(currentBoard);
  yield fork(init);
  yield fork(save);
  yield fork(add);
  yield fork(remove);
  yield fork(invalidate);
  yield fork(resetChanges);
}



// WEBPACK FOOTER //
// ../redux/store/configuration/project/editor/board/saga.js